/** The admin's Change status dialog and the author's Cancel request dialog. */

import { useState } from 'react';

import { CancelIn, StatusIn, cancelInReasonOneMax, statusInNoteOneMax } from '@/api/generated/schemas';
import { useCancelFeedback, useSetFeedbackStatus } from '@/api/queries';
import type { FeedbackAdminStatus, FeedbackDetail } from '@/api/types';
import { bannerError, fieldErrors, serverFieldErrors } from '@/api/validate';
import { ActionButton, buttonState } from '@/ui/ActionButton';
import { FieldError, fieldA11y } from '@/ui/FieldError';
import { FieldLabel } from '@/ui/FieldLabel';
import { Modal } from '@/ui/Modal';
import { FormError } from '@/ui/StateCard';

import { ADMIN_STATUSES, STATUS } from './feedbackUi';

export function StatusDialog({ item, onClose }: { item: FeedbackDetail; onClose: () => void }) {
  const setStatus = useSetFeedbackStatus();
  const choices = ADMIN_STATUSES.filter((s) => s.value !== item.status);
  const [status, setStatusValue] = useState<FeedbackAdminStatus>(choices[0]?.value ?? 'in_review');
  const [note, setNote] = useState('');
  const invalid = Object.keys(fieldErrors(StatusIn, { status, note })).length > 0;
  const server = serverFieldErrors(setStatus.error);

  return (
    <Modal
      title="Change status"
      subtitle={`Now ${STATUS[item.status].label}. The change, and your note, show on the thread for ${item.author.display_name}.`}
      size="narrow"
      onClose={onClose}
      onSubmit={() => setStatus.mutate({ id: item.id, status, note: note.trim() || undefined }, { onSuccess: onClose })}
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <ActionButton state={buttonState(setStatus)} label="Change status" pendingLabel="Saving…" disabled={invalid} />
        </>
      }
    >
      <div className="stack-md">
        <FormError error={bannerError(setStatus.error, ['note'])} />
        <div className="field">
          <FieldLabel htmlFor="feedback-status" id="feedback-status-hint" label="New status" hint={`${STATUS[status].hint}.`} />
          <select
            id="feedback-status"
            autoFocus
            value={status}
            aria-describedby="feedback-status-hint"
            onChange={(e) => setStatusValue(e.target.value as FeedbackAdminStatus)}
          >
            {choices.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor="feedback-status-note">Note</label>
          <textarea
            id="feedback-status-note"
            rows={4}
            maxLength={statusInNoteOneMax}
            value={note}
            placeholder="Optional: what was done, or why"
            {...fieldA11y('feedback-status-note', server.note)}
            onChange={(e) => setNote(e.target.value)}
          />
          <FieldError inputId="feedback-status-note" message={server.note} />
        </div>
      </div>
    </Modal>
  );
}

export function CancelDialog({ item, onClose }: { item: FeedbackDetail; onClose: () => void }) {
  const cancel = useCancelFeedback();
  const [reason, setReason] = useState('');
  const invalid = Object.keys(fieldErrors(CancelIn, { reason })).length > 0;
  const server = serverFieldErrors(cancel.error);

  return (
    <Modal
      title="Cancel this request?"
      subtitle="It is closed and takes no more comments. An admin can still reopen it."
      size="narrow"
      onClose={onClose}
      onSubmit={() => cancel.mutate({ id: item.id, reason: reason.trim() || undefined }, { onSuccess: onClose })}
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>Keep it</button>
          <ActionButton state={buttonState(cancel)} className="btn btn-danger" label="Cancel request" pendingLabel="Cancelling…" doneLabel="Cancelled" disabled={invalid} />
        </>
      }
    >
      <div className="stack-md">
        <FormError error={bannerError(cancel.error, Object.keys(CancelIn.shape))} />
        <div className="field">
          <label htmlFor="feedback-cancel-reason">Reason</label>
          <textarea
            id="feedback-cancel-reason"
            rows={3}
            autoFocus
            maxLength={cancelInReasonOneMax}
            value={reason}
            placeholder="Optional: why it is no longer needed"
            {...fieldA11y('feedback-cancel-reason', server.reason)}
            onChange={(e) => setReason(e.target.value)}
          />
          <FieldError inputId="feedback-cancel-reason" message={server.reason} />
        </div>
      </div>
    </Modal>
  );
}
